import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Layout from 'components/Layout';
import {
  Grid,
  Typography,
  Button as MuiButton,
  IconButton,
  CircularProgress,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Select,
  MenuItem,
  Paper,
  Portal,
  Slide,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useMst } from 'model/Root';
import { CORESKILLS_DEFAULT_ORG_ID, OCR_MAX_PDF_BYTES } from 'config/constants';
import { useNavigate } from 'react-router-dom';
import type { RootInstance } from 'model/Root';
import type { OcrStoreInstance } from 'model/CoreSkills/OcrStore';
import { getAudiobookDetails, triggerTocExtraction } from 'services/csAudiobookApi';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import FormFieldText from 'components/FormComponents/FormFieldText';
import FormFieldDate from 'components/FormComponents/FormFieldDate';
import FormFieldNumber from 'components/FormComponents/FormFieldNumber';
import FormFieldTextArea from 'components/FormComponents/FormFieldTextArea';
import FormFieldCheckboxList from 'components/FormComponents/FormFieldCheckboxList';
import FormFieldSwitch from 'components/FormComponents/FormFieldSwitch';
import ImageUploadButton from 'components/Button/ImageUploadButton';
import FileUploadButton from 'components/Button/FileUploadButton';
import { Audiobook, MediaTypes } from 'types';
import { AudiobookDefaults } from 'utils/defaults';
import { getAudiobookValidationSchema } from 'utils/audiobookValidation';

type CsAudiobookListItem = {
  id: string;
  title?: string;
  name?: string;
  author?: string;
  language?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  toc_extraction_status?: 'idle' | 'running' | 'failed' | 'done';
  toc_count?: number;
  chapters_count?: number;
  has_chapters?: boolean;
  review_required?: boolean;
};

type OcrDoc = { id: string; filename?: string; created_at?: string; updated_at?: string };

type SortMode = 'date_desc' | 'date_asc' | 'name_asc' | 'name_desc';

type CreateAudiobookFormValues = Audiobook & { addToPublicList: boolean };

const createAudiobookFormInitialValues = (): CreateAudiobookFormValues => ({
  ...AudiobookDefaults,
  language: 'fi',
  mediaTypeId: MediaTypes.Audiobook,
  categoryIds: [],
  tagIds: [],
  targetGroupIds: [],
  ebookStartSkip: null,
  ebookEndSkip: null,
  addToPublicList: true,
});

const normalizeCsLanguage = (lang?: string | null) => {
  if (!lang) return 'fi';
  const trimmed = lang.trim().toLowerCase();
  const mapping: Record<string, string> = {
    fi: 'fi',
    suomi: 'fi',
    finnish: 'fi',
    sv: 'sv',
    swe: 'sv',
    se: 'sv',
    svenska: 'sv',
    en: 'en',
    eng: 'en',
    english: 'en',
  };
  if (mapping[trimmed]) return mapping[trimmed];
  if (trimmed.startsWith('fi')) return 'fi';
  if (trimmed.startsWith('sv') || trimmed.startsWith('sve') || trimmed.startsWith('ruo')) return 'sv';
  if (trimmed.startsWith('en') || trimmed.startsWith('ing')) return 'en';
  return trimmed.slice(0, 2) || 'fi';
};

function AudiobooksListScreen() {
  const { t } = useTranslation();
  const { csAudiobookStore, ocrStore, errorStore, categoryStore, tagStore, targetGroupStore, audiobookStore } =
    useMst() as RootInstance;
  const [fetchedOnce, setFetchedOnce] = useState(false);
  const loading = !!csAudiobookStore.loadingByKey.get('list');
  const navigate = useNavigate();

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [isCreatingChapters, setIsCreatingChapters] = useState(false);
  const [tocProgress, setTocProgress] = useState<{ total: number; completed: number; message?: string } | null>(null);
  const tocPollRef = useRef<number | null>(null);
  const [formInitialValues, setFormInitialValues] = useState<CreateAudiobookFormValues>(() =>
    createAudiobookFormInitialValues()
  );
  const [formKey, setFormKey] = useState(0);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | undefined>();
  const [selectedEbookFile, setSelectedEbookFile] = useState<File | undefined>();
  const [selectedPdfBookFile, setSelectedPdfBookFile] = useState<File | undefined>();
  const { categories, getCategories } = categoryStore;
  const { tags, getTags } = tagStore;
  const { targetGroups, getTargetGroups } = targetGroupStore;
  const { createAudiobook, getAllAudiobooks } = audiobookStore;
  const handleDialogImageSelection = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target?.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    setSelectedImage(file);
    setImagePreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      return URL.createObjectURL(file);
    });
  }, []);
  const handleCreateDialogSubmit = async (
    values: CreateAudiobookFormValues,
    helpers: FormikHelpers<CreateAudiobookFormValues>
  ) => {
    if (!selectedDocId) {
      setDocError('common_required');
      helpers.setSubmitting(false);
      return;
    }
    setDocError(null);
    setIsCreatingChapters(true);
    try {
      const normalizedLanguage = normalizeCsLanguage(values.language);
      console.log('[CreateAudiobook] Start', {
        selectedDocId,
        language: normalizedLanguage,
        title: values.name,
        author: values.author,
      });
      console.time?.('[CreateAudiobook] total');
      const id = await ocrStore.createFromOcr(selectedDocId, {
        language: normalizedLanguage,
        title: values.name || undefined,
        author: values.author || undefined,
        organization_id: CORESKILLS_DEFAULT_ORG_ID,
      });
      if (!id) return;
      console.log('[CreateAudiobook] Created audiobook', { id });

      const { addToPublicList, ...audiobookValues } = values;
      let publicAudiobookId: string | null = null;

      if (addToPublicList) {
        const payload: Audiobook = {
          ...audiobookValues,
          published: false,
          mediaTypeId: audiobookValues.mediaTypeId ?? MediaTypes.Audiobook,
        };
        const publicAudiobook = (await createAudiobook(
          payload,
          [],
          [],
          selectedImage,
          selectedEbookFile,
          selectedPdfBookFile
        )) as { id?: number | string } | { data?: { id?: number | string } } | null;
        publicAudiobookId = extractPublicAudiobookId(publicAudiobook);
        if (publicAudiobookId) {
          try {
            await csAudiobookStore.linkPublicAudiobook(id, publicAudiobookId);
          } catch (linkErr) {
            console.warn('[CreateAudiobook] Failed to link public audiobook', linkErr);
            errorStore.setError('Audiobook link failed, please retry.');
          }
        } else {
          console.warn('[CreateAudiobook] No public audiobook ID returned; skipping link');
        }
        void getAllAudiobooks();
      } else {
        console.log('[CreateAudiobook] Skipping public audiobook draft');
      }

      try {
        await triggerTocExtraction(id);
        console.log('[CreateAudiobook] Triggered TOC extraction', { id });
      } catch (_) {
        console.warn('[CreateAudiobook] TOC trigger failed, will poll details', { id });
      }
      try {
        type ListRunsResponse = { items?: Array<{ id?: string; status?: string; startedAt?: string }> };
        const runsApi = csAudiobookStore as unknown as {
          listRuns?: (aid: string, type?: 'toc_extraction') => Promise<unknown>;
          getRun?: (
            aid: string,
            rid: string
          ) => Promise<{
            data: {
              id: string;
              status: 'running' | 'completed' | 'failed' | 'cancelled';
              totalChapters?: number;
              completedChapters?: number;
              lastMessage?: string;
            };
          } | null>;
        };
        if (runsApi?.listRuns) {
          const resUnknown = (await runsApi.listRuns(id, 'toc_extraction')) as unknown as ListRunsResponse;
          const items = Array.isArray(resUnknown?.items) ? resUnknown.items : [];
          let chosen = items.find((r) => String(r.status).toLowerCase() === 'running');
          if (!chosen && items.length > 0) {
            chosen = items.slice().sort((a, b) => Date.parse(b.startedAt || '') - Date.parse(a.startedAt || ''))[0];
          }
          if (chosen?.id && runsApi?.getRun) {
            const runId = chosen.id;
            console.log('[CreateAudiobook] Found TOC run', { runId });
            if (tocPollRef.current) {
              window.clearInterval(tocPollRef.current);
              tocPollRef.current = null;
            }
            tocPollRef.current = window.setInterval(
              async () => {
                try {
                  const getRunFn = runsApi.getRun;
                  if (!getRunFn) {
                    return;
                  }
                  const run = await getRunFn(id, runId);
                  const data = run?.data;
                  if (data) {
                    const total = Number(data.totalChapters ?? 0);
                    const completed = Number(data.completedChapters ?? 0);
                    setTocProgress({
                      total,
                      completed,
                      message: data.lastMessage,
                    });
                    if (data.status !== 'running' || (total > 0 && completed >= total)) {
                      if (tocPollRef.current) {
                        window.clearInterval(tocPollRef.current);
                        tocPollRef.current = null;
                      }
                    }
                  }
                } catch (e) {
                  // ignore single poll errors
                }
              },
              4500 + Math.floor(Math.random() * 1000)
            );
          } else {
            console.log('[CreateAudiobook] No TOC run found; fallback to indeterminate');
          }
        }
      } catch (e) {
        console.warn('[CreateAudiobook] Runs progress unavailable', e);
      }
      const timeoutAt = Date.now() + 60000;
      console.log('[CreateAudiobook] Polling TOC…');
      while (Date.now() < timeoutAt) {
        try {
          const res = await getAudiobookDetails(id);
          const data = (res as { data?: { toc?: unknown[] } })?.data ?? {};
          const arr = Array.isArray(data.toc) ? data.toc : [];
          if (arr.length > 0) break;
        } catch (_) {
          // retry silently
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      try {
        await csAudiobookStore.getDetails(id);
        console.log('[CreateAudiobook] Details refreshed, navigating to TOC', { id });
      } catch (_) {
        void 0;
      }
      setCreateOpen(false);
      navigate(`/cs-audiobooks/${id}/toc`);
      console.timeEnd?.('[CreateAudiobook] total');
    } finally {
      if (tocPollRef.current) {
        window.clearInterval(tocPollRef.current);
        tocPollRef.current = null;
      }
      setTocProgress(null);
      setIsCreatingChapters(false);
      helpers.setSubmitting(false);
    }
  };

  // Audiobook list controls
  const [sortMode, setSortMode] = useState<SortMode>('date_desc');
  const [search, setSearch] = useState('');

  // PDF modal controls
  const [ocrSearch, setOcrSearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteAudiobookId, setConfirmDeleteAudiobookId] = useState<string | null>(null);
  const [isDeletingAudiobook, setIsDeletingAudiobook] = useState(false);
  const [allFilesOpen, setAllFilesOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Hydrate from session cache for instant first paint
        type Hydratable = { hydrateFromCache?: () => void; itemsArray: unknown[] };
        const s = csAudiobookStore as unknown as Hydratable;
        if (typeof s.hydrateFromCache === 'function') {
          s.hydrateFromCache();
          if (mounted && Array.isArray(s.itemsArray) && s.itemsArray.length > 0) {
            setFetchedOnce(true);
          }
        }
        // Load light list to keep UI fast; will update cache
        await csAudiobookStore.listAll({ limit: 50, light: true });
        if (mounted) setFetchedOnce(true);
        // Enrich in background with full data (timestamps, etc.)
        void csAudiobookStore.listAll({ force: true, limit: 50, light: false, background: true });
        // Warm up OCR list quietly so the modal opens instantly
        if (ocrStore?.list) {
          void ocrStore.list({ documentType: 'audiobook', limit: 50, suppressError: true, background: true });
        }
      } finally {
        // no-op
      }
    })();
    return () => {
      mounted = false;
    };
  }, [csAudiobookStore, ocrStore]);

  useEffect(() => {
    if (!createOpen) return;
    setFormInitialValues(createAudiobookFormInitialValues());
    setFormKey((prev) => prev + 1);
    setDocError(null);
    void getCategories(MediaTypes.Audiobook);
    void getTags(MediaTypes.Audiobook);
    void getTargetGroups();
  }, [createOpen, getCategories, getTags, getTargetGroups]);

  useEffect(() => {
    if (createOpen) return;
    setSelectedDocId(null);
    setDocError(null);
    setSelectedImage(undefined);
    setSelectedEbookFile(undefined);
    setSelectedPdfBookFile(undefined);
    setFormInitialValues(createAudiobookFormInitialValues());
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImagePreviewUrl(null);
  }, [createOpen, imagePreviewUrl]);

  const filteredItems = useMemo(() => {
    const arr = (csAudiobookStore.itemsArray as unknown as CsAudiobookListItem[]) ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((x) => {
      const t = String(x.title ?? x.name ?? x.id ?? '').toLowerCase();
      const a = String(x.author ?? '').toLowerCase();
      return t.includes(q) || a.includes(q);
    });
  }, [csAudiobookStore.itemsArray, search]);

  const sortedItems = useMemo(() => {
    const arr = [...filteredItems];
    const getTime = (x: Partial<CsAudiobookListItem>) =>
      Date.parse(
        (x.updated_at as string) ||
          ((x as unknown as Record<string, unknown>)?.['updatedAt'] as string | undefined) ||
          (x.created_at as string) ||
          ((x as unknown as Record<string, unknown>)?.['createdAt'] as string | undefined) ||
          ''
      ) || 0;
    const getName = (x: Partial<CsAudiobookListItem>) => String(x.title ?? x.name ?? x.id ?? '').toLowerCase();
    return arr.sort((a, b) => {
      if (sortMode === 'name_asc' || sortMode === 'name_desc') {
        const na = getName(a);
        const nb = getName(b);
        if (na !== nb) return sortMode === 'name_asc' ? na.localeCompare(nb) : nb.localeCompare(na);
        const bt = getTime(b);
        const at = getTime(a);
        if (bt !== at) return bt - at;
        return 0;
      }
      const bt = getTime(b);
      const at = getTime(a);
      if (bt !== at) return sortMode === 'date_desc' ? bt - at : at - bt;
      const bc =
        Date.parse(
          (b.created_at as string) ||
            ((b as unknown as Record<string, unknown>)?.['createdAt'] as string | undefined) ||
            ''
        ) || 0;
      const ac =
        Date.parse(
          (a.created_at as string) ||
            ((a as unknown as Record<string, unknown>)?.['createdAt'] as string | undefined) ||
            ''
        ) || 0;
      if (bc !== ac) return sortMode === 'date_desc' ? bc - ac : ac - bc;
      return getName(a).localeCompare(getName(b));
    });
  }, [filteredItems, sortMode]);

  const sortedOcrDocs = useMemo(() => {
    const docs = ((ocrStore?.itemsArray ?? []) as unknown as OcrDoc[]).slice();
    return docs.sort((a: OcrDoc, b: OcrDoc) => {
      const at = Date.parse(a.updated_at || a.created_at || '') || 0;
      const bt = Date.parse(b.updated_at || b.created_at || '') || 0;
      return bt - at;
    });
  }, [ocrStore?.itemsArray]);

  const docErrorMessage = useMemo(() => {
    if (!docError) return null;
    return docError === 'common_required' ? t('common_required') : docError;
  }, [docError, t]);

  const handleDocSelect = useCallback(
    (docId: string) => {
      setSelectedDocId(docId);
      setDocError(null);
    },
    [setSelectedDocId, setDocError]
  );

  // All files modal derives from OCR store
  const ocrFiltered = useMemo(() => {
    const arr = (ocrStore?.itemsArray ?? []) as unknown as OcrDoc[];
    const q = ocrSearch.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((d) =>
      String(d.filename || '')
        .toLowerCase()
        .includes(q)
    );
  }, [ocrStore?.itemsArray, ocrSearch]);

  const ocrVisible = useMemo(() => ocrFiltered, [ocrFiltered]);

  // Modal fetch handled on open click to fetch full list (effect removed as no-op)

  return (
    <Layout title={'Äänikirjat'}>
      <Grid container spacing={2}>
        {/* Create new audiobook */}
        <Grid item xs={12}>
          <Box
            sx={{
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: 1,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle1">Luo uusi äänikirja</Typography>
              <Typography variant="caption" color="GrayText">
                Valitse kirjan PDF‑tiedosto ja syötä perustiedot
              </Typography>
            </Box>
            <MuiButton
              variant="contained"
              onClick={async () => {
                setCreateOpen(true);
                setSelectedDocId(null);
                setDocError(null);
                const ocr = ocrStore as unknown as OcrStoreInstance;
                const now = Date.now();
                const isStale = !ocr.lastFetchedAt || now - (ocr.lastFetchedAt as number) > 30000;
                if (!ocr.itemsArray || ocr.itemsArray.length === 0 || isStale) {
                  setCreateLoading(true);
                  try {
                    await ocrStore.list({ documentType: 'audiobook', limit: 50, suppressError: true });
                  } finally {
                    setCreateLoading(false);
                  }
                }
              }}
            >
              Luo äänikirja
            </MuiButton>
          </Box>
        </Grid>

        {/* Upload PDF and open list */}
        <Grid item xs={12}>
          <Box
            sx={{
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: 1,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle1">Kirjan PDF‑tiedostot</Typography>
              <Typography variant="caption" color="GrayText">
                Lataa uusia PDF‑tiedostoja ja hallitse olemassa olevia tiedostoja
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0] ?? null;
                  if (!file) {
                    setPendingFile(null);
                    return;
                  }
                  if (!file.name.toLowerCase().endsWith('.pdf')) {
                    errorStore.setError('Invalid file type');
                    setPendingFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                  }
                  if (file.size > OCR_MAX_PDF_BYTES) {
                    errorStore.setError('File too large');
                    setPendingFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                  }
                  setPendingFile(file);
                }}
              />
              <MuiButton
                size="small"
                variant="contained"
                disabled={!pendingFile || ocrStore?.isUploading}
                onClick={async () => {
                  if (!pendingFile || !ocrStore?.uploadPdf) return;
                  await ocrStore.uploadPdf(pendingFile, { document_type: 'audiobook' });
                  setPendingFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                  // Warm refresh in background so next open has fresh list
                  if (ocrStore?.list) {
                    void ocrStore.list({
                      documentType: 'audiobook',
                      background: true,
                      noCache: true,
                      suppressError: true,
                    });
                  }
                }}
              >
                Lataa
              </MuiButton>
              {ocrStore?.isUploading && <CircularProgress size={16} />}
              <MuiButton
                size="small"
                variant="outlined"
                onClick={async () => {
                  setAllFilesOpen(true);
                  setModalLoading(true);
                  const s = ocrStore as unknown as OcrStoreInstance;
                  try {
                    // First page
                    await ocrStore.list({ documentType: 'audiobook', limit: 200, noCache: true, suppressError: true });
                    // Subsequent pages with safety stops
                    for (let i = 0; i < 20; i++) {
                      if (!s.hasNextPage) break;
                      const prevId = s.nextCursorId ?? null;
                      const prevUpd = s.nextCursorUpdatedAt ?? null;
                      try {
                        await ocrStore.list({
                          documentType: 'audiobook',
                          limit: 200,
                          cursorUpdatedAt: s.nextCursorUpdatedAt ?? undefined,
                          cursorId: s.nextCursorId ?? undefined,
                          append: true,
                          suppressError: true,
                        });
                      } catch (_) {
                        break;
                      }
                      const nextId = s.nextCursorId ?? null;
                      const nextUpd = s.nextCursorUpdatedAt ?? null;
                      if (!nextId || !nextUpd || (nextId === prevId && nextUpd === prevUpd)) break;
                      await new Promise((r) => setTimeout(r, 150));
                    }
                  } finally {
                    setModalLoading(false);
                  }
                }}
              >
                Näytä PDF‑tiedostot
              </MuiButton>
            </Box>
          </Box>
        </Grid>

        {/* Books list header */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">Nykyiset äänikirjat</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Select size="small" value={sortMode} onChange={(e) => setSortMode(e.target.value as SortMode)}>
                <MenuItem value="date_desc">Uusin ensin</MenuItem>
                <MenuItem value="date_asc">Vanhin ensin</MenuItem>
                <MenuItem value="name_asc">Nimi A–Ö</MenuItem>
                <MenuItem value="name_desc">Nimi Ö–A</MenuItem>
              </Select>
              <TextField
                size="small"
                placeholder="Hae äänikirjoja"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              {fetchedOnce && loading && <CircularProgress size={14} />}
              <IconButton
                color="default"
                onClick={() => {
                  void csAudiobookStore.listAll({ force: true, limit: 50, light: true });
                  void csAudiobookStore.listAll({ force: true, limit: 50, light: false, background: true });
                }}
                aria-label="refresh"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Grid>

        {/* Books list */}
        {!fetchedOnce ? (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={20} />
              <Typography>Ladataan…</Typography>
            </Box>
          </Grid>
        ) : sortedItems.length === 0 && !loading ? (
          <Grid item xs={12}>
            <Typography variant="body2" color="GrayText">
              No audiobooks
            </Typography>
          </Grid>
        ) : (
          sortedItems.map((ab: CsAudiobookListItem) => {
            const statusRaw = String(ab.status ?? '').toLowerCase();
            const chaptersEnabled = statusRaw === 'chapters_created' || statusRaw === 'completed';
            const tocEnabled = !chaptersEnabled;
            const statusChip = (() => {
              if (statusRaw === 'completed') return <Chip size="small" label={'Valmis'} color="success" />;
              if (statusRaw === 'chapters_created') return <Chip size="small" label={'Muokkaa lukuja'} color="info" />;
              if (statusRaw === 'processing') return <Chip size="small" label={'Tarkista TOC'} color="warning" />;
              return <Chip size="small" label={'Luotu'} />;
            })();
            return (
              <Grid item xs={12} key={ab.id}>
                <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, p: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body1" sx={{ mr: 2 }}>
                      {ab.title ?? ab.name ?? ab.id}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {statusChip}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setConfirmDeleteAudiobookId(String(ab.id))}
                        aria-label="delete-audiobook"
                        disabled={isDeletingAudiobook}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="GrayText">
                    {ab.author ?? ''} • {ab.language ?? ''} • {ab.status ?? ''}
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <MuiButton
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                      onClick={() => navigate(`/cs-audiobooks/${ab.id}/toc`)}
                      disabled={!tocEnabled}
                    >
                      TOC
                    </MuiButton>
                    <MuiButton
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`/cs-audiobooks/${ab.id}/chapters`)}
                      disabled={!chaptersEnabled}
                    >
                      Luvut
                    </MuiButton>
                  </Box>
                </Box>
              </Grid>
            );
          })
        )}
      </Grid>

      {/* All files modal */}
      <Dialog
        open={allFilesOpen}
        onClose={() => setAllFilesOpen(false)}
        fullWidth
        maxWidth="md"
        keepMounted
        disableAutoFocus
        disableEnforceFocus
        transitionDuration={{ appear: 0, enter: 0, exit: 0 }}
      >
        <DialogTitle>Kaikki kirjan PDF‑tiedostot</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Hae tiedostoja"
              value={ocrSearch}
              onChange={(e) => setOcrSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <IconButton
              color="default"
              onClick={() => {
                if (!ocrStore.list) return;
                setModalLoading(true);
                const s = ocrStore as unknown as OcrStoreInstance;
                (async () => {
                  try {
                    await ocrStore.list({ documentType: 'audiobook', limit: 200, noCache: true, suppressError: true });
                    for (let i = 0; i < 20; i++) {
                      if (!s.hasNextPage) break;
                      const prevId = s.nextCursorId ?? null;
                      const prevUpd = s.nextCursorUpdatedAt ?? null;
                      try {
                        await ocrStore.list({
                          documentType: 'audiobook',
                          limit: 200,
                          cursorUpdatedAt: s.nextCursorUpdatedAt ?? undefined,
                          cursorId: s.nextCursorId ?? undefined,
                          append: true,
                          suppressError: true,
                        });
                      } catch (_) {
                        break;
                      }
                      const nextId = s.nextCursorId ?? null;
                      const nextUpd = s.nextCursorUpdatedAt ?? null;
                      if (!nextId || !nextUpd || (nextId === prevId && nextUpd === prevUpd)) break;
                      await new Promise((r) => setTimeout(r, 150));
                    }
                  } finally {
                    setModalLoading(false);
                  }
                })();
              }}
              aria-label="refresh"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          {modalLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="GrayText">
                Ladataan…
              </Typography>
            </Box>
          )}
          <Grid container spacing={1}>
            {ocrVisible.map((doc) => (
              <Grid item xs={12} key={doc.id}>
                <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, p: 1.5 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {doc.filename}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmDeleteId(String(doc.id))}
                      aria-label="delete"
                      disabled={isDeleting}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="caption" color="GrayText">
                    {doc.created_at ?? ''}
                  </Typography>
                </Box>
              </Grid>
            ))}
            {ocrVisible.length === 0 && !modalLoading && (
              <Grid item xs={12}>
                <Typography variant="body2" color="GrayText">
                  Ei tiedostoja
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setAllFilesOpen(false)}>Sulje</MuiButton>
        </DialogActions>
      </Dialog>

      {/* Confirm delete dialog for PDF */}
      <Dialog open={!!confirmDeleteId} onClose={() => (isDeleting ? void 0 : setConfirmDeleteId(null))}>
        <DialogTitle>Poista kirjan PDF‑tiedosto?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Tämä poistaa tiedoston pysyvästi. Haluatko jatkaa?</Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton disabled={isDeleting} onClick={() => setConfirmDeleteId(null)}>
            Peruuta
          </MuiButton>
          <MuiButton
            color="error"
            variant="contained"
            disabled={isDeleting}
            onClick={async () => {
              if (!confirmDeleteId || !ocrStore?.remove) return;
              setIsDeleting(true);
              try {
                await ocrStore.remove(confirmDeleteId);
                setConfirmDeleteId(null);
                if (ocrStore?.list) void ocrStore.list({ documentType: 'audiobook', limit: 200 });
              } finally {
                setIsDeleting(false);
              }
            }}
          >
            {isDeleting ? <CircularProgress size={16} /> : 'Poista'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Confirm delete dialog for Audiobook */}
      <Dialog
        open={!!confirmDeleteAudiobookId}
        onClose={() => (isDeletingAudiobook ? void 0 : setConfirmDeleteAudiobookId(null))}
      >
        <DialogTitle>Poista äänikirja?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Tämä poistaa äänikirjan pysyvästi. Haluatko jatkaa?</Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton disabled={isDeletingAudiobook} onClick={() => setConfirmDeleteAudiobookId(null)}>
            Peruuta
          </MuiButton>
          <MuiButton
            color="error"
            variant="contained"
            disabled={isDeletingAudiobook}
            onClick={async () => {
              type CsStoreWithRemove = { remove?: (id: string) => Promise<unknown> };
              const storeWithRemove = csAudiobookStore as unknown as CsStoreWithRemove;
              if (!confirmDeleteAudiobookId || !storeWithRemove.remove) return;
              setIsDeletingAudiobook(true);
              try {
                await storeWithRemove.remove(confirmDeleteAudiobookId);
                setConfirmDeleteAudiobookId(null);
              } finally {
                setIsDeletingAudiobook(false);
              }
            }}
          >
            {isDeletingAudiobook ? <CircularProgress size={16} /> : 'Poista'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => (isCreatingChapters ? void 0 : setCreateOpen(false))}
        fullWidth
        maxWidth="lg"
      >
        <Formik<CreateAudiobookFormValues>
          key={formKey}
          initialValues={formInitialValues}
          enableReinitialize
          validationSchema={Yup.lazy((values: Audiobook) => getAudiobookValidationSchema(t, values))}
          onSubmit={handleCreateDialogSubmit}
        >
          {({ values, setFieldValue, submitForm, isSubmitting }) => (
            <>
              <DialogTitle
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  pr: 2,
                }}
              >
                <Typography variant="h6" component="div">
                  Luo äänikirja
                </Typography>
                <FormFieldSwitch name="addToPublicList" label="Lisää luonnoksena kirjalistaan" />
              </DialogTitle>
              <DialogContent dividers sx={{ maxHeight: '80vh' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <CreateAudiobookDocPicker
                      docs={sortedOcrDocs}
                      isLoading={createLoading || !!ocrStore?.isLoading}
                      onSelect={handleDocSelect}
                      selectedDocId={selectedDocId}
                      errorMessage={docErrorMessage}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Tiedot
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormFieldText label={t('audiobook_name')} name="name" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormFieldTextArea label={t('audiobook_description')} name="description" rows={4} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormFieldText label={t('audiobook_author')} name="author" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormFieldText label={t('audiobook_publisher')} name="publisher" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormFieldDate label={t('release_date')} name="releaseDate" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormFieldNumber label={t('audiobook_releaseYear')} name="releaseYear" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormFieldText label={t('audiobook_language')} name="language" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormFieldText label={t('audiobook_reader')} name="reader" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormFieldText label={t('audiobook_shop_url')} name="shopUrl" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormFieldText label={t('audiobook_isbn')} name="isbn" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormFieldSwitch name="isTimeTracked" label={t('audiobook_track_time')} />
                      </Grid>
                      <Grid item xs={12}>
                        <ImageUploadButton
                          text="page_image"
                          imagePreviewUrl={imagePreviewUrl}
                          handleImageSelection={handleDialogImageSelection}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FileUploadButton
                          text="book_ebook_file"
                          idPrefix="cs-audiobook"
                          src={values?.ebook}
                          mimeType="application/epub+zip"
                          handleSelection={(event) => {
                            if (event.target.files) {
                              setFieldValue('ebook', event.target.files[0].name);
                              setSelectedEbookFile(event.target.files[0]);
                            }
                          }}
                          handleRemove={() => {
                            setFieldValue('ebook', null);
                            setSelectedEbookFile(undefined);
                          }}
                          emptyText="ebook_not_set"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FileUploadButton
                          text="book_pdfBook_file"
                          idPrefix="cs-audiobook-pdf"
                          src={values?.pdfBook}
                          mimeType="application/pdf"
                          handleSelection={(event) => {
                            if (event.target.files) {
                              setFieldValue('pdfBook', event.target.files[0].name);
                              setSelectedPdfBookFile(event.target.files[0]);
                            }
                          }}
                          handleRemove={() => {
                            setFieldValue('pdfBook', null);
                            setSelectedPdfBookFile(undefined);
                          }}
                          emptyText="pdfBook_not_set"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormFieldCheckboxList
                          label={t('categories')}
                          name="categoryIds"
                          listItems={categories ?? []}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormFieldCheckboxList
                          label={t('target_groups')}
                          name="targetGroupIds"
                          listItems={targetGroups ?? []}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormFieldCheckboxList label={t('tags')} nameField="tag" name="tagIds" listItems={tags ?? []} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <MuiButton
                  onClick={() => (!isCreatingChapters ? setCreateOpen(false) : null)}
                  disabled={isCreatingChapters}
                >
                  {t('cancel')}
                </MuiButton>
                <MuiButton
                  variant="contained"
                  onClick={() => {
                    if (!selectedDocId) {
                      setDocError('common_required');
                      return;
                    }
                    setFieldValue('published', false);
                    void submitForm();
                  }}
                  disabled={!selectedDocId || isCreatingChapters || isSubmitting}
                >
                  {isCreatingChapters ? 'Luodaan…' : 'Luo'}
                </MuiButton>
              </DialogActions>
            </>
          )}
        </Formik>
      </Dialog>
      <CreateAudiobookProgressNotice open={isCreatingChapters} message={tocProgress?.message} />
    </Layout>
  );
}

export default observer(AudiobooksListScreen);

const extractPublicAudiobookId = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null;
  const directId = (payload as { id?: number | string }).id;
  if (directId !== undefined && directId !== null) {
    const str = String(directId).trim();
    return str.length > 0 ? str : null;
  }
  const nestedId = (payload as { data?: { id?: number | string } }).data?.id;
  if (nestedId !== undefined && nestedId !== null) {
    const str = String(nestedId).trim();
    return str.length > 0 ? str : null;
  }
  return null;
};

type CreateAudiobookDocPickerProps = {
  docs: OcrDoc[];
  isLoading: boolean;
  selectedDocId: string | null;
  onSelect: (id: string) => void;
  errorMessage: string | null;
};

const CreateAudiobookDocPicker = React.memo(
  ({ docs, isLoading, selectedDocId, onSelect, errorMessage }: CreateAudiobookDocPickerProps) => (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Valitse kirjan PDF‑tiedosto
      </Typography>
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="GrayText">
            Ladataan…
          </Typography>
        </Box>
      )}
      <List dense sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid rgba(0,0,0,0.12)' }}>
        {docs.map((doc) => (
          <ListItem key={doc.id} disableGutters>
            <ListItemButton
              selected={String(selectedDocId) === String(doc.id)}
              onClick={() => onSelect(String(doc.id))}
            >
              <ListItemText primary={doc.filename} secondary={doc.created_at ?? ''} />
            </ListItemButton>
          </ListItem>
        ))}
        {!isLoading && docs.length === 0 && (
          <ListItem>
            <ListItemText
              primary={
                <Typography variant="body2" color="GrayText">
                  Ei tiedostoja
                </Typography>
              }
            />
          </ListItem>
        )}
      </List>
      {errorMessage && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {errorMessage}
        </Typography>
      )}
    </>
  )
);

CreateAudiobookDocPicker.displayName = 'CreateAudiobookDocPicker';

type CreateAudiobookProgressNoticeProps = {
  open: boolean;
  message?: string | null;
};

const CreateAudiobookProgressNotice = React.memo(({ open, message }: CreateAudiobookProgressNoticeProps) => (
  <Portal>
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(640px, calc(100% - 32px))',
          zIndex: (theme) => theme.zIndex.snackbar,
        }}
      >
        <Paper elevation={6} sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {message || 'Luodaan äänikirjaa… Tunnistetaan sisällysluetteloa'}
          </Typography>
          <LinearProgress />
        </Paper>
      </Box>
    </Slide>
  </Portal>
));

CreateAudiobookProgressNotice.displayName = 'CreateAudiobookProgressNotice';
